import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Facebook, Linkedin, Music2, MessageCircle, ShoppingBag, Store, MapPin, FileText, Leaf, Gift } from "lucide-react";
import { Reveal } from "@/components/motion";
import { SOCIALS, SHOPS, MAPS_URL } from "@/lib/site";
import { useLang } from "@/context/LangContext";

const ICONS = { instagram: Instagram, facebook: Facebook, linkedin: Linkedin, tiktok: Music2, whatsapp: MessageCircle, shopee: ShoppingBag, tokopedia: Store };

const GROUPS = [
  {
    title: ["Jelajahi Minaqu", "Explore Minaqu"],
    links: [
      { id: "about", label: "About Minaqu Indonesia", sub: ["Company profile & owner", "Company profile & owner"], to: "/tentang-kami", Icon: FileText },
      { id: "plants", label: "Plants Catalogue Minaqu", sub: ["137 tanaman · harga Rupiah", "137 plants · prices in Rupiah"], to: "/katalog-tanaman", Icon: Leaf },
      { id: "souvenir", label: "Souvenir Catalogue Minaqu", sub: ["Tissue culture souvenir & hampers", "Tissue culture souvenirs & hampers"], to: "/souvenir-hampers", Icon: Gift },
      { id: "location", label: "Minaqu Location", sub: ["Pondok Bitung, Bogor", "Pondok Bitung, Bogor, Indonesia"], href: MAPS_URL, Icon: MapPin },
    ],
  },
  { title: ["Contact Us", "Contact Us"], links: SOCIALS.map((s) => ({ id: s.id, label: s.label, sub: s.handle, href: s.href, Icon: ICONS[s.id] })) },
  { title: ["Order Here", "Order Here"], links: SHOPS.map((s) => ({ id: s.id, label: s.label, sub: s.handle, href: s.href, Icon: ICONS[s.id] })) },
];

const Row = ({ l, n }) => {
  const { t } = useLang();
  const sub = Array.isArray(l.sub) ? t(l.sub[0], l.sub[1]) : l.sub;
  const inner = (
    <>
      <span className="absolute inset-0 origin-left scale-x-0 bg-[var(--leaf)] transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-x-100" />
      <span className="relative font-display text-xs font-bold text-[var(--leaf)] transition-colors group-hover:text-white/70 w-6">{String(n).padStart(2, "0")}</span>
      <span className="relative grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[var(--mint)] text-[var(--leaf)] transition-colors group-hover:bg-white"><l.Icon className="h-[18px] w-[18px]" /></span>
      <span className="relative min-w-0 flex-1">
        <span className="block truncate font-display text-base sm:text-lg font-extrabold text-[var(--ink)] transition-colors group-hover:text-white">{l.label}</span>
        <span className="block truncate text-xs sm:text-sm text-[var(--muted-ink)] transition-colors group-hover:text-white/80">{sub}</span>
      </span>
      <ArrowUpRight className="relative h-5 w-5 shrink-0 text-[var(--leaf)] transition-all duration-300 group-hover:rotate-45 group-hover:text-white" />
    </>
  );
  const cls = "group relative flex items-center gap-3 sm:gap-4 overflow-hidden rounded-full border border-leaf/20 bg-white px-4 sm:px-5 py-3";
  return l.to
    ? <Link to={l.to} data-testid={`linkhub-${l.id}`} className={cls}>{inner}</Link>
    : <a href={l.href} target="_blank" rel="noreferrer" data-testid={`linkhub-${l.id}`} className={cls}>{inner}</a>;
};

export const LinkHub = () => {
  let n = 0;
  const { t } = useLang();
  return (
    <section data-testid="home-linkhub" className="relative bg-[var(--paper)] py-24 sm:py-32">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <img src="/assets/img/logo-linktree.png" alt="minaqu.indonesia" className="h-24 w-24 rounded-full bg-white object-contain p-2 ring-1 ring-leaf/15" />
              <p className="mt-6 eyebrow">@minaqu.indonesia</p>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight">{t("Satu pintu", "One door")}<br />{t("untuk semua", "to all of")}<br /><span className="text-[var(--leaf)]">Minaqu.</span></h2>
              <p className="mt-6 max-w-md text-base text-[var(--muted-ink)]">{t("Welcome to Minaqu Indonesia! Order top-quality ornamental plants today. Semua tautan Linktree kami — katalog, lokasi, media sosial, dan toko — kini ada di sini.", "Welcome to Minaqu Indonesia! Order top-quality ornamental plants today. All our Linktree links — catalogues, location, social media and shops — are now in one place.")}</p>
            </Reveal>
          </div>
        </div>
        <div className="lg:col-span-7 space-y-12">
          {GROUPS.map((g) => (
            <Reveal key={g.title[0]}>
              <p className="mb-4 font-display text-sm font-black uppercase tracking-[0.2em] text-[var(--ink)]">{t(g.title[0], g.title[1])}</p>
              <div className="grid gap-3">{g.links.map((l) => <Row key={l.id} l={l} n={++n} />)}</div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
